import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Fuse from 'fuse.js';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import type { MenuCategory, MenuItem, MenuItemPortion, SpiceLevel } from '../types/menu';
import SpiceSelector from './SpiceSelector';
import StandalonePortionSelector from './StandalonePortionSelector';
import CloseButton from './CloseButton';
import MenuItemImage from './MenuItemImage';
import { DietaryDot } from './DietaryDot';
import {
  ChevronDownIcon,
  ShoppingBagIcon,
  UserIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ListBulletIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import WindowsScrollArrows from './WindowsScrollArrows';
import { formatCurrency } from '../config/currency';
import type { HeldOrder } from '../types/heldOrder';
import { saveHeldOrder, deleteHeldOrder } from '../utils/heldOrderStore';
import { PauseIcon } from '@heroicons/react/24/outline';
import { CURRENCY } from '../config/currency';


interface CartItem {
  menuItemId: number;
  quantity: number;
  price: number;
  comment?: string;
  toppingIds?: number[];
  menuItemDetails: MenuItem;
  portionId?: number;
  portionName?: string;
  requestedSpiceLevel?: SpiceLevel;
}

interface ImprovedVendorOrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  tableName: string;
  onOrderPlaced?: (newOrder?: any) => void;
  existingOrderId?: number;
  resumeHeldOrder?: HeldOrder | null;
  onHeldOrderChange?: () => void;
}



// Item style helper for modernDashboard theme - warm muted colors
const getItemStyle = (itemName: string) => {
  const name = itemName.toLowerCase();
  const styles: Record<string, { emoji: string; color: string; gradient: string }> = {
    // Beverages - soft muted
    coffee: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    tea: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    chai: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    juice: { emoji: '🧃', color: '#C2410C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    smoothie: { emoji: '🥤', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    water: { emoji: '💧', color: '#0369A1', gradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' },
    soda: { emoji: '🥤', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    cola: { emoji: '🥤', color: '#92400E', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    beer: { emoji: '🍺', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    wine: { emoji: '🍷', color: '#991B1B', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    lassi: { emoji: '🥛', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    milkshake: { emoji: '🥛', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    lemonade: { emoji: '🍋', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    mojito: { emoji: '🍸', color: '#15803D', gradient: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' },
    // Fast food - warm muted
    burger: { emoji: '🍔', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    pizza: { emoji: '🍕', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    pasta: { emoji: '🍝', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    noodle: { emoji: '🍜', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    momo: { emoji: '🥟', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    dumpling: { emoji: '🥟', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    spring: { emoji: '🥠', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    // Chinese - warm muted
    manchuria: { emoji: '🥘', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    manchurian: { emoji: '🥘', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    schezwan: { emoji: '🌶️', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    szechwan: { emoji: '🌶️', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    chilli: { emoji: '🌶️', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    fried: { emoji: '🍳', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    gobi: { emoji: '🥦', color: '#15803D', gradient: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' },
    corn: { emoji: '🌽', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    babycorn: { emoji: '🌽', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    mushroom: { emoji: '🍄', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    // Rice & Indian - warm muted
    rice: { emoji: '🍚', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    biryani: { emoji: '🍛', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    pulao: { emoji: '🍚', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    curry: { emoji: '🍛', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    masala: { emoji: '🍛', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    tikka: { emoji: '🍢', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    tandoor: { emoji: '🍢', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    kebab: { emoji: '🍢', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    chicken: { emoji: '🍗', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    mutton: { emoji: '🥩', color: '#991B1B', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    lamb: { emoji: '🥩', color: '#991B1B', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    prawn: { emoji: '🦐', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    shrimp: { emoji: '🦐', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    fish: { emoji: '🐟', color: '#0369A1', gradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' },
    salad: { emoji: '🥗', color: '#15803D', gradient: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' },
    soup: { emoji: '🍲', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    sandwich: { emoji: '🥪', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    wrap: { emoji: '🌯', color: '#65A30D', gradient: 'linear-gradient(135deg, #ECFCCB 0%, #D9F99D 100%)' },
    fries: { emoji: '🍟', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Korean
    korean: { emoji: '🍜', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    kimchi: { emoji: '🥬', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    ramen: { emoji: '🍜', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    // Desserts - warm muted
    cake: { emoji: '🍰', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    ice: { emoji: '🍨', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    dessert: { emoji: '🍮', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    gulab: { emoji: '🧁', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    halwa: { emoji: '🍮', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    kheer: { emoji: '🍚', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    jalebi: { emoji: '🍩', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Indian breads - warm muted
    dosa: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    idli: { emoji: '⚪', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    paratha: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    roti: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    naan: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    kulcha: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Other - warm muted
    egg: { emoji: '🍳', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    omelette: { emoji: '🍳', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    paneer: { emoji: '🧀', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    panner: { emoji: '🧀', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    dal: { emoji: '🥘', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    chole: { emoji: '🥘', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    rajma: { emoji: '🥘', color: '#991B1B', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    aloo: { emoji: '🥔', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    potato: { emoji: '🥔', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    samosa: { emoji: '🔺', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    pakora: { emoji: '🍘', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    chaat: { emoji: '🥗', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    drink: { emoji: '🥤', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    beverage: { emoji: '🥤', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    meal: { emoji: '🍽️', color: '#6366F1', gradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' },
    combo: { emoji: '🍱', color: '#7C3AED', gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)' },
    thali: { emoji: '🍱', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    platter: { emoji: '🍱', color: '#7C3AED', gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)' },
  };
  for (const [keyword, style] of Object.entries(styles)) {
    if (name.includes(keyword)) return style;
  }
  return { emoji: '🍽️', color: '#6366F1', gradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' };
};

// Category emoji helper
const getCategoryEmoji = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('drink') || name.includes('beverage')) return '🍹';
  if (name.includes('main') || name.includes('course')) return '🍛';
  if (name.includes('starter') || name.includes('appetizer')) return '🥗';
  if (name.includes('dessert') || name.includes('sweet')) return '🍰';
  if (name.includes('breakfast') || name.includes('morning')) return '🌅';
  if (name.includes('lunch')) return '☀️';
  if (name.includes('dinner') || name.includes('evening')) return '🌙';
  if (name.includes('weekend')) return '🎉';
  if (name.includes('snack')) return '🍿';
  if (name.includes('biryani')) return '🍛';
  if (name.includes('pizza')) return '🍕';
  if (name.includes('burger')) return '🍔';
  if (name.includes('chinese') || name.includes('noodle')) return '🥡';
  if (name.includes('south') || name.includes('dosa') || name.includes('idli')) return '🫓';
  return '🍽️';
};

const ImprovedVendorOrderFormModal: React.FC<ImprovedVendorOrderFormModalProps> = ({
  isOpen,
  onClose,
  tableId,
  tableName,
  onOrderPlaced,
  existingOrderId,
  resumeHeldOrder,
  onHeldOrderChange
}) => {
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const { user } = useAuth();

  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  // Dietary filter for order-taking ("what veg options?"). VEG includes VEGAN.
  const [dietaryFilter, setDietaryFilter] = useState<'ALL' | 'VEG' | 'NON_VEG' | 'VEGAN' | 'GLUTEN_FREE'>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCart, setShowCart] = useState<boolean>(false);
  const [selectedPortions, setSelectedPortions] = useState<{ [key: number]: MenuItemPortion }>({});
  const [selectedSpiceLevels, setSelectedSpiceLevels] = useState<{ [key: number]: SpiceLevel }>({});
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Tax configuration
  interface TaxConfig {
    cgstRate: number;
    sgstRate: number;
    igstRate: number;
    serviceChargeRate: number;
    serviceChargeEnabled: boolean;
    taxType: 'INTRA_STATE' | 'INTER_STATE';
    /** VAT-inclusive countries (GB) — standard rate when no override */
    vatRate?: number;
  }
  const [taxConfig, setTaxConfig] = useState<TaxConfig | null>(null);

  const cartSheetRef = useRef<HTMLDivElement>(null);

  // Customer details for walk-in orders
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [showCustomerInfo, setShowCustomerInfo] = useState<boolean>(false);
  const [specialInstructions, setSpecialInstructions] = useState<string>('');
  const [showSpecialInstructions, setShowSpecialInstructions] = useState<boolean>(false);

  // Held order tracking — only deleted on successful placement
  const [resumedHeldOrderId, setResumedHeldOrderId] = useState<string | null>(null);

  // Pre-populate cart when resuming a held order
  useEffect(() => {
    if (isOpen && resumeHeldOrder) {
      setCart(resumeHeldOrder.items as CartItem[]);
      setCustomerName(resumeHeldOrder.customerName || '');
      setCustomerPhone(resumeHeldOrder.customerPhone || '');
      setSpecialInstructions(resumeHeldOrder.specialInstructions || '');
      if (resumeHeldOrder.customerName || resumeHeldOrder.customerPhone) {
        setShowCustomerInfo(true);
      }
      if (resumeHeldOrder.specialInstructions) {
        setShowSpecialInstructions(true);
      }
      setResumedHeldOrderId(resumeHeldOrder.id);
      setShowCart(true);
    }
  }, [isOpen, resumeHeldOrder]);

  // Track a history entry so browser back closes the modal instead of navigating away
  const hasPushedHistoryRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    // Push a lightweight state once to intercept Back and close the modal
    if (!hasPushedHistoryRef.current) {
      try {
        window.history.pushState({ orderModal: true }, '');
        hasPushedHistoryRef.current = true;
      } catch { }
    }
    const onPopState = () => {
      // Close the modal, and immediately re-push state so a second Back goes to the previous page
      onClose();
      try {
        window.history.pushState({ orderModal: true }, '');
        hasPushedHistoryRef.current = true;
      } catch { }
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      hasPushedHistoryRef.current = false;
    };
  }, [isOpen, onClose]);

  const handleClose = () => {
    onClose();
  };

  // Hide bottom nav when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('ordering-modal-open');
      fetchMenu();
      fetchTaxConfig();
      setShowCart(false);
    } else {
      document.body.classList.remove('ordering-modal-open');
    }
    return () => {
      document.body.classList.remove('ordering-modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-focus search on desktop when menu finishes loading
  useEffect(() => {
    if (!loading && isOpen && menuCategories.length > 0 && window.innerWidth >= 768) {
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [loading, isOpen, menuCategories.length]);

  const fetchTaxConfig = async () => {
    try {
      const response = await api.get('/api/vendor/tax-config');
      if (response.data) {
        setTaxConfig({
          cgstRate: response.data.cgstRate || 0,
          sgstRate: response.data.sgstRate || 0,
          igstRate: response.data.igstRate || 0,
          serviceChargeRate: response.data.serviceChargeRate || 0,
          serviceChargeEnabled: response.data.serviceChargeEnabled || false,
          taxType: response.data.taxType || 'INTRA_STATE',
          // VAT-inclusive countries (GB): standard rate applies when no override
          vatRate: response.data.vatRate ?? 20
        });
      }
    } catch (err) {
      console.error('Failed to fetch tax config:', err);
      setTaxConfig(null);
    }
  };

  const fetchMenu = async () => {
    if (!user || !user.id) {
      setError('You must be logged in to view the menu');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      const isSteward = user.roles?.includes('ROLE_STEWARD');
      const isNumericTableId = tableId && /^\d+$/.test(String(tableId));

      if (isNumericTableId) {
        try {
          response = await api.get(`/api/menu/table/${tableId}`);
        } catch (tableMenuError) {
          console.log('Table-specific menu failed, falling back:', tableMenuError);
          response = null;
        }
      }

      if (!response) {
        const isTakeaway = tableId && !/^\d+$/.test(String(tableId)) && String(tableId).toUpperCase().includes('TAKEAWAY');

        if (isSteward) {
          try {
            response = await api.get('/api/menu');
          } catch {
            throw new Error('Failed to load menu for steward');
          }
        } else {
          const menuUrl = isTakeaway
            ? `/vendors/${user.id}/menu?orderType=TAKEAWAY`
            : `/vendors/${user.id}/menu`;
          response = await api.get(menuUrl);
        }
      }

      const categoriesWithItems: MenuCategory[] = response.data || [];

      const sortByCreation = <T extends { [key: string]: any }>(a: T, b: T) => {
        const ao = (a as any).displayOrder ?? (a as any).createdAt ?? (a as any).id ?? 0;
        const bo = (b as any).displayOrder ?? (b as any).createdAt ?? (b as any).id ?? 0;
        const av = typeof ao === 'string' && !Number.isFinite(+ao) ? new Date(ao).getTime() : Number(ao);
        const bv = typeof bo === 'string' && !Number.isFinite(+bo) ? new Date(bo).getTime() : Number(bo);
        return av - bv;
      };

      const sortedCategories = [...categoriesWithItems].sort(sortByCreation);

      const processedCategories = sortedCategories.map(category => ({
        ...category,
        items: (category.items || [])
          .slice()
          .sort(sortByCreation)
          .map(item => {
            if (item.portions && item.portions.length > 0) {
              const processedPortions = item.portions.map(portion => ({
                ...portion,
                effectivePrice: item.price + portion.priceModifier,
                displayPrice: `${CURRENCY.symbol}${(item.price + portion.priceModifier).toFixed(2)}`
              }));
              return { ...item, portions: processedPortions };
            }
            return item;
          })
      }));

      setMenuCategories(processedCategories);

      if (processedCategories.length > 0) {
        setSelectedCategory(processedCategories[0].id);
      }
    } catch (err) {
      setError('Failed to load menu items');
      console.error('Error fetching menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item: MenuItem) => {
    const selectedPortion = selectedPortions[item.id] ||
      item.portions?.find(p => p.isDefault) ||
      item.portions?.[0];

    const effectivePrice = selectedPortion
      ? item.price + selectedPortion.priceModifier
      : item.price;

    const requestedSpiceLevel = selectedSpiceLevels[item.id] || item.spiceLevel;

    const existingItem = cart.find(ci =>
      ci.menuItemId === item.id &&
      ci.portionId === selectedPortion?.id
    );

    if (existingItem) {
      updateCartItemQuantity(item.id, 1, selectedPortion?.id);
    } else {
      const cartItem: CartItem = {
        menuItemId: item.id,
        quantity: 1,
        price: effectivePrice,
        menuItemDetails: item,
        portionId: selectedPortion?.id,
        portionName: selectedPortion?.portionName,
        requestedSpiceLevel: item.allowsSpiceCustomization ? requestedSpiceLevel : undefined
      };
      setCart(prev => [...prev, cartItem]);

      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      toast.success(item.name, {
        duration: 1200,
        position: 'top-center',
        style: {
          background: theme.primary,
          color: 'white',
          borderRadius: '50px',
          padding: '8px 16px',
          fontSize: '13px'
        }
      });
    }
  };

  const removeFromCart = (itemId: number, portionId?: number) => {
    setCart(prev => prev.filter(item =>
      !(item.menuItemId === itemId && item.portionId === portionId)
    ));
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  const updateCartItemQuantity = (itemId: number, delta: number, portionId?: number) => {
    setCart(prev => prev.map(item => {
      if (item.menuItemId === itemId && item.portionId === portionId) {
        const newQuantity = Math.max(0, item.quantity + delta);
        return newQuantity === 0 ? null : { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean) as CartItem[]);
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // VAT-inclusive countries (GB): menu prices already CONTAIN VAT — nothing is
  // added except service charge, and we surface the VAT included for info.
  // Matches the backend's applyVatInclusiveTaxes so the preview equals the
  // placed order's total. GST preview (IN) is untouched.
  const isVatInclusive = CURRENCY.code === 'GBP';

  const calculateTaxes = () => {
    const subtotal = calculateSubtotal();
    if (!taxConfig) return { cgst: 0, sgst: 0, igst: 0, serviceCharge: 0, vatIncluded: 0, total: 0 };

    let cgst = 0, sgst = 0, igst = 0, serviceCharge = 0, vatIncluded = 0;

    if (isVatInclusive) {
      if (taxConfig.serviceChargeEnabled) {
        serviceCharge = subtotal * (taxConfig.serviceChargeRate / 100);
      }
      const vatRate = taxConfig.vatRate ?? 20;
      vatIncluded = subtotal * (vatRate / (100 + vatRate));
      return { cgst, sgst, igst, serviceCharge, vatIncluded, total: serviceCharge };
    }

    if (taxConfig.taxType === 'INTER_STATE') {
      igst = subtotal * (taxConfig.igstRate / 100);
    } else {
      cgst = subtotal * (taxConfig.cgstRate / 100);
      sgst = subtotal * (taxConfig.sgstRate / 100);
    }

    if (taxConfig.serviceChargeEnabled) {
      serviceCharge = subtotal * (taxConfig.serviceChargeRate / 100);
    }

    return {
      cgst,
      sgst,
      igst,
      serviceCharge,
      vatIncluded,
      total: cgst + sgst + igst + serviceCharge
    };
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const taxes = calculateTaxes();
    return subtotal + taxes.total;
  };

  const getItemQuantityInCart = (itemId: number, portionId?: number) => {
    const item = cart.find(ci =>
      ci.menuItemId === itemId &&
      ci.portionId === portionId
    );
    return item?.quantity || 0;
  };

  const getTotalItemsInCart = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handleHoldOrder = () => {
    if (cart.length === 0) return;
    const isTakeaway = tableId === 'TAKEAWAY';
    const itemCount = cart.reduce((n, i) => n + i.quantity, 0);
    const label = isTakeaway
      ? `Takeaway${customerName.trim() ? ` — ${customerName.trim()}` : ` — ${itemCount} item${itemCount !== 1 ? 's' : ''}`}`
      : `${tableName} — ${itemCount} item${itemCount !== 1 ? 's' : ''}`;

    const held: HeldOrder = {
      id: crypto.randomUUID(),
      tableId: isTakeaway ? null : Number(tableId),
      tableName: isTakeaway ? 'Takeaway' : tableName,
      orderType: isTakeaway ? 'takeaway' : 'dine-in',
      items: cart,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      specialInstructions: specialInstructions.trim(),
      label,
      createdAt: Date.now(),
      vendorId: user?.id ?? 0,
    };

    saveHeldOrder(held);

    // If we resumed a held order and now re-hold, delete the old one
    if (resumedHeldOrderId) {
      deleteHeldOrder(resumedHeldOrderId);
      setResumedHeldOrderId(null);
    }

    // Reset cart and close
    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
    setShowCustomerInfo(false);
    setSpecialInstructions('');
    setShowCart(false);
    onClose();
    onHeldOrderChange?.();
    toast.success(`Order held: ${label}`, {
      icon: '⏸',
      style: { borderRadius: '10px', background: 'var(--status-warn-text)', color: 'var(--text-inverse)' },
    });
  };

  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Please add items to the cart');
      return;
    }

    if (!user || !user.id) {
      toast.error('You must be logged in to place an order');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const orderData = {
        items: cart.map((item) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          comment: item.comment || undefined,
          toppingIds: item.toppingIds,
          portionId: item.portionId,
          portionName: item.portionName
        })),
        customerName: customerName.trim() || undefined,
        customerPhone: customerPhone.trim() || undefined,
        orderNotes: specialInstructions.trim() || (showCustomerInfo && (customerName.trim() || customerPhone.trim())
          ? `Walk-in customer order`
          : undefined)
      };

      let response;
      if (existingOrderId) {
        response = await api.put(`/orders/${existingOrderId}`, orderData);
      } else if (tableId === 'TAKEAWAY') {
        response = await api.post(`/api/tables/takeaway/orders`, orderData);
      } else {
        response = await api.post(`/api/tables/${tableId}/orders`, orderData);
      }

      toast.success(tableId === 'TAKEAWAY' ? 'Takeaway order placed!' : 'Order placed successfully!', {
        icon: tableId === 'TAKEAWAY' ? '🥡' : '🎉',
        style: {
          borderRadius: '10px',
          background: theme.success || '#10b981',
          color: 'var(--text-inverse)',
        }
      });

      // Clean up held order on successful placement
      if (resumedHeldOrderId) {
        deleteHeldOrder(resumedHeldOrderId);
        setResumedHeldOrderId(null);
        onHeldOrderChange?.();
      }

      if (onOrderPlaced) {
        onOrderPlaced(response?.data);
      }

      setCart([]);
      setCustomerName('');
      setCustomerPhone('');
      setShowCustomerInfo(false);
      setSpecialInstructions('');
      setShowCart(false);
      onClose();
    } catch (err) {
      console.error('Order submission error:', err);
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Order submission issue';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Fuzzy search configuration
  const fuseOptions = useMemo(() => ({
    keys: [
      { name: 'name', weight: 0.7 },
      { name: 'description', weight: 0.3 }
    ],
    threshold: 0.4,
    distance: 100,
    minMatchCharLength: 2,
    includeScore: true,
    ignoreLocation: true,
    useExtendedSearch: false
  }), []);

  const fuse = useMemo(() => {
    const allItems = menuCategories.flatMap(category =>
      (category.items || []).map(item => ({
        ...item,
        categoryName: category.name,
        categoryId: category.id
      }))
    );
    return new Fuse(allItems, fuseOptions);
  }, [menuCategories, fuseOptions]);

  // Get flat list of items for the current view
  const getFilteredItems = useCallback((): MenuItem[] => {
    const matchesDietary = (i: MenuItem) => {
      if (dietaryFilter === 'ALL') return true;
      // Gluten-free rides the allergen axis: hide dishes that DECLARE gluten.
      // Declaration-based (same semantic as the customer allergen filter) —
      // undeclared dishes still show; staff confirm serious allergies verbally.
      if (dietaryFilter === 'GLUTEN_FREE') return !i.allergens?.includes('GLUTEN');
      if (dietaryFilter === 'VEG') return i.dietaryType === 'VEG' || i.dietaryType === 'VEGAN';
      return i.dietaryType === dietaryFilter;
    };

    let base: MenuItem[];
    if (searchQuery && searchQuery.length >= 2) {
      const fuseResults = fuse.search(searchQuery);
      base = fuseResults.map(result => {
        const { categoryName, categoryId: _, ...cleanItem } = result.item as MenuItem & { categoryId: number; categoryName: string };
        return cleanItem as MenuItem;
      });
    } else if (!selectedCategory) {
      // Show all items
      base = menuCategories.flatMap(cat => cat.items || []);
    } else {
      const category = menuCategories.find(cat => cat.id === selectedCategory);
      base = category?.items || [];
    }
    return base.filter(matchesDietary);
  }, [searchQuery, selectedCategory, menuCategories, fuse, dietaryFilter]);

  const filteredItems = getFilteredItems();

  // Compact dietary dropdown — native <select> overlaid on a styled chip so
  // phones get the OS picker. Neutral when ALL, soft ochre-tinted when active.
  const DIETARY_FILTER_LABELS: Record<string, string> = { ALL: 'All diets', VEG: 'Veg', NON_VEG: 'Non-veg', VEGAN: 'Vegan', GLUTEN_FREE: 'Gluten-free' };
  const dietaryFilterControl = (
    <div
      className="relative flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium whitespace-nowrap"
      style={{
        backgroundColor: dietaryFilter === 'ALL' ? '#F3F4F6' : `${theme.primary}1A`,
        color: dietaryFilter === 'ALL' ? '#6B7280' : theme.primary,
        border: dietaryFilter === 'ALL' ? '1px solid transparent' : `1.5px solid ${theme.primary}`,
      }}
    >
      {dietaryFilter !== 'ALL' && dietaryFilter !== 'GLUTEN_FREE' && <DietaryDot type={dietaryFilter} />}
      {dietaryFilter === 'GLUTEN_FREE' && <span aria-hidden="true" style={{ fontSize: '11px', lineHeight: 1 }}>🌾</span>}
      <span>{DIETARY_FILTER_LABELS[dietaryFilter]}</span>
      <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.127l3.71-3.896a.75.75 0 111.08 1.04l-4.24 4.454a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
      </svg>
      <select
        value={dietaryFilter}
        onChange={(e) => setDietaryFilter(e.target.value as typeof dietaryFilter)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label="Filter by dietary type"
      >
        <option value="ALL">All diets</option>
        <option value="VEG">Veg</option>
        <option value="NON_VEG">Non-veg</option>
        <option value="VEGAN">Vegan</option>
        <option value="GLUTEN_FREE">Gluten-free</option>
      </select>
    </div>
  );

  // Check if item needs portion selector on first tap
  const handleItemTap = useCallback((item: MenuItem) => {
    if (item.isAvailable === false) return;

    const hasMultiplePortions = item.portions && item.portions.length > 1;
    const hasSpiceCustomization = item.allowsSpiceCustomization;

    // If item has multiple portions, always open portion selector
    if (hasMultiplePortions) {
      setOpenDropdown(item.id);
      return;
    }

    // If item has spice customization and not yet in cart, open selector
    if (hasSpiceCustomization && !cart.find(ci => ci.menuItemId === item.id)) {
      setOpenDropdown(item.id);
      return;
    }

    // Simple add
    handleAddToCart(item);
  }, [cart, handleAddToCart]);

  // Get total quantity of an item across all portions
  const getTotalItemQuantity = useCallback((itemId: number) => {
    return cart
      .filter(ci => ci.menuItemId === itemId)
      .reduce((sum, ci) => sum + ci.quantity, 0);
  }, [cart]);

  // ─── Cart Sidebar Content (shared between desktop sidebar and mobile sheet) ───
  const renderCartContent = (inSheet = false) => {
    const totalItems = getTotalItemsInCart();

    return (
      <>
        {/* Cart Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="flex items-center gap-2">
            <ShoppingBagIcon className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Cart
            </span>
            {totalItems > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--action)', color: 'white' }}>
                {totalItems}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {totalItems > 0 && (
              <button
                onClick={() => { setCart([]); if (navigator.vibrate) navigator.vibrate(30); }}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors hover:bg-red-50"
                style={{ color: 'var(--status-bad)', border: 'none', background: 'transparent' }}
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
            {inSheet && <CloseButton onClick={() => setShowCart(false)} size="sm" />}
          </div>
        </div>

        {/* Cart Items - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-3 py-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8 text-center">
              <ShoppingBagIcon className="w-10 h-10 mb-2" style={{ color: '#D1D5DB' }} /> {/* basil-todo */}
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Cart is empty</p>
              <p className="text-xs mt-1" style={{ color: '#D1D5DB' }}>Tap items to add</p> {/* basil-todo */}
            </div>
          ) : (
            <div className="space-y-2">
              {cart.map((item, index) => {
                const itemStyle = getItemStyle(item.menuItemDetails?.name || '');
                return (
                  <div
                    key={`${item.menuItemId}-${item.portionId || 'default'}-${index}`}
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)' }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                          <DietaryDot type={item.menuItemDetails?.dietaryType} />
                          {item.menuItemDetails?.name || 'Unknown'}
                        </p>
                        {item.portionName && (
                          <span className="text-xs px-1.5 py-0.5 rounded mt-0.5 inline-block" style={{ backgroundColor: 'var(--bg-sunken)', color: 'var(--text-tertiary)' }}>
                            {item.portionName}
                          </span>
                        )}
                        {item.requestedSpiceLevel && (
                          <span className="text-xs ml-1 px-1.5 py-0.5 rounded mt-0.5 inline-block" style={{ backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' }}>
                            {item.requestedSpiceLevel}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.menuItemId, item.portionId)}
                        className="p-1 rounded hover:bg-red-50 transition-colors flex-shrink-0"
                        style={{ color: 'var(--status-bad)', border: 'none', background: 'transparent' }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      {/* Quantity stepper */}
                      <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
                        <button
                          onClick={() => updateCartItemQuantity(item.menuItemId, -1, item.portionId)}
                          className="w-7 h-7 flex items-center justify-center text-sm font-medium"
                          style={{ backgroundColor: 'var(--bg-sunken)', border: 'none', color: 'var(--text-secondary)' }}
                        >
                          -
                        </button>
                        <span className="w-7 h-7 flex items-center justify-center text-sm font-bold" style={{ color: 'var(--text-primary)', backgroundColor: 'white' }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartItemQuantity(item.menuItemId, 1, item.portionId)}
                          className="w-7 h-7 flex items-center justify-center text-sm font-medium"
                          style={{ backgroundColor: 'var(--action)', border: 'none', color: 'white' }}
                        >
                          +
                        </button>
                      </div>
                      <div className="text-right">
                        {item.quantity > 1 && (
                          <span className="text-[10px] block" style={{ color: 'var(--text-tertiary)' }}>
                            {formatCurrency(item.price, { showDecimals: false })} x {item.quantity}
                          </span>
                        )}
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {formatCurrency(item.price * item.quantity, { showDecimals: false })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Customer info + Special instructions (collapsible) */}
        {cart.length > 0 && (
          <div className="flex-shrink-0 border-t px-3 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
            {/* Customer Info Toggle */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowCustomerInfo(!showCustomerInfo); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex items-center justify-between w-full px-2 py-1.5 rounded-lg text-xs cursor-pointer select-none"
              style={{ backgroundColor: showCustomerInfo ? '#F0F9FF' : '#F8FAFC', border: '1px solid var(--border-subtle)' }}
            >
              <div className="flex items-center gap-1.5 pointer-events-none">
                <UserIcon className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
                <span style={{ color: 'var(--text-secondary)' }}>Customer Info</span>
              </div>
              <ChevronDownIcon
                className={`w-3.5 h-3.5 transition-transform pointer-events-none ${showCustomerInfo ? 'rotate-180' : ''}`}
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>
            <AnimatePresence>
              {showCustomerInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-2 space-y-2 overflow-hidden"
                >
                  <input
                    type="text"
                    placeholder="Customer Name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 transition-all"
                    style={{ borderColor: 'var(--border-subtle)', '--tw-ring-color': theme.primary } as React.CSSProperties}
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 transition-all"
                    style={{ borderColor: 'var(--border-subtle)', '--tw-ring-color': theme.primary } as React.CSSProperties}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Special Instructions Toggle */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowSpecialInstructions(!showSpecialInstructions); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex items-center justify-between w-full px-2 py-1.5 rounded-lg text-xs cursor-pointer select-none mt-2"
              style={{ backgroundColor: showSpecialInstructions ? '#F0F9FF' : '#F8FAFC', border: '1px solid var(--border-subtle)' }}
            >
              <div className="flex items-center gap-1.5 pointer-events-none">
                <ListBulletIcon className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
                <span style={{ color: 'var(--text-secondary)' }}>Special Instructions</span>
              </div>
              <ChevronDownIcon
                className={`w-3.5 h-3.5 transition-transform pointer-events-none ${showSpecialInstructions ? 'rotate-180' : ''}`}
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>
            <AnimatePresence>
              {showSpecialInstructions && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-2 overflow-hidden"
                >
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="e.g. No onions, extra spicy"
                    className="w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 transition-all resize-none"
                    style={{ borderColor: 'var(--border-subtle)', '--tw-ring-color': theme.primary } as React.CSSProperties}
                    rows={2}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Tax Summary */}
        {cart.length > 0 && (
          <div className="flex-shrink-0 px-4 py-2 border-t" style={{ borderColor: 'var(--border-subtle)', backgroundColor: '#FAFBFC' }}> {/* basil-todo */}
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-tertiary)' }}>Subtotal</span>
                <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{formatCurrency(calculateSubtotal())}</span>
              </div>
              {!isVatInclusive && taxConfig && taxConfig.taxType === 'INTRA_STATE' && (taxConfig.cgstRate > 0 || taxConfig.sgstRate > 0) && (
                <>
                  <div className="flex justify-between" style={{ color: 'var(--text-tertiary)' }}>
                    <span>CGST ({taxConfig.cgstRate}%)</span>
                    <span>{formatCurrency(calculateTaxes().cgst)}</span>
                  </div>
                  <div className="flex justify-between" style={{ color: 'var(--text-tertiary)' }}>
                    <span>SGST ({taxConfig.sgstRate}%)</span>
                    <span>{formatCurrency(calculateTaxes().sgst)}</span>
                  </div>
                </>
              )}
              {!isVatInclusive && taxConfig && taxConfig.taxType === 'INTER_STATE' && taxConfig.igstRate > 0 && (
                <div className="flex justify-between" style={{ color: 'var(--text-tertiary)' }}>
                  <span>IGST ({taxConfig.igstRate}%)</span>
                  <span>{formatCurrency(calculateTaxes().igst)}</span>
                </div>
              )}
              {taxConfig && taxConfig.serviceChargeEnabled && taxConfig.serviceChargeRate > 0 && (
                <div className="flex justify-between" style={{ color: 'var(--text-tertiary)' }}>
                  <span>Service ({taxConfig.serviceChargeRate}%)</span>
                  <span>{formatCurrency(calculateTaxes().serviceCharge)}</span>
                </div>
              )}
              <div className="flex justify-between pt-1 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>Total</span>
                <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{formatCurrency(calculateGrandTotal())}</span>
              </div>
              {isVatInclusive && taxConfig && (
                <div className="flex justify-between" style={{ color: 'var(--text-tertiary)' }}>
                  <span>Includes VAT ({taxConfig.vatRate ?? 20}%)</span>
                  <span>{formatCurrency(calculateTaxes().vatIncluded)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hold + Place Order Buttons */}
        {cart.length > 0 && (
          <div className="flex-shrink-0 px-4 py-3 flex flex-col gap-2" style={{ backgroundColor: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)', paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            {/* Hold Order button */}
            {!existingOrderId && (
              <button
                onClick={handleHoldOrder}
                className="w-full font-semibold text-xs transition-all flex items-center justify-center"
                style={{
                  background: 'var(--status-warn-bg)',
                  border: '1px solid var(--status-warn-text)',
                  color: 'var(--status-warn-text)',
                  borderRadius: '10px',
                  height: '38px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <PauseIcon className="w-4 h-4" />
                <span>Hold Order</span>
              </button>
            )}
            {/* Place Order button */}
            <button
              onClick={handleSubmitOrder}
              disabled={submitting || cart.length === 0}
              className="w-full font-semibold text-sm transition-all flex items-center justify-center"
              style={{
                background: submitting ? '#D1D5DB' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
                border: 'none',
                color: 'var(--text-inverse)',
                boxShadow: submitting ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.35)',
                borderRadius: '10px',
                height: '44px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textAlign: 'center',
              }}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" style={{ flexShrink: 0 }}></div>
                  <span>Placing...</span>
                </>
              ) : (
                <>
                  <span>Place Order</span>
                  <span style={{ opacity: 0.6 }}>|</span>
                  <span className="font-bold">{formatCurrency(calculateGrandTotal())}</span>
                </>
              )}
            </button>
          </div>
        )}
      </>
    );
  };

  // ─── RENDER ───

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Global styles for hiding scrollbars and bottom nav override */}
          <style>
            {`
              .ordering-modal-open .vendor-bottom-nav { display: none !important; }
              .pos-hide-scrollbar { -ms-overflow-style: none !important; scrollbar-width: none !important; }
              .pos-hide-scrollbar::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
            `}
          </style>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-2 md:p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 100000 }}
            onClick={handleClose}
          >
            {/* Modal Container */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full h-full sm:h-[95vh] sm:max-w-[98vw] sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
              style={{
                backgroundColor: theme.background,
                paddingTop: window.innerWidth >= 640 ? '0px' : 'max(20px, env(safe-area-inset-top))',
                boxSizing: 'border-box'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* ─── Header: Title + Search + Close ─── */}
              <div
                className="flex items-center gap-3 px-3 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-subtle)', backgroundColor: theme.background }}
              >
                <CloseButton onClick={handleClose} size="md" />
                <div className="flex items-center gap-2">
                  <h2 className="font-bold text-base whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                    {tableId === 'TAKEAWAY' ? '🥡 Takeaway' : `🍽️ ${tableName}`}
                  </h2>
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{
                      backgroundColor: tableId === 'TAKEAWAY' ? '#FEF3C7' : '#DCFCE7',
                      color: tableId === 'TAKEAWAY' ? '#92400E' : '#15803D',
                    }}
                  >
                    {tableId === 'TAKEAWAY' ? 'Takeaway' : 'Dine-in'}
                  </span>
                </div>
                <div className="relative flex-1">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{
                      backgroundColor: 'var(--bg-sunken)',
                      borderColor: 'var(--border-subtle)',
                      color: 'var(--text-secondary)',
                      '--tw-ring-color': theme.primary
                    } as React.CSSProperties}
                  />
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                </div>
              </div>

              {/* ─── Main Content ─── */}
              {loading ? (
                <div className="flex items-center justify-center flex-1">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" style={{ borderBottomColor: theme.primary }}></div>
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading menu...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center flex-1 p-8">
                  <div className="text-center">
                    <p className="text-red-500 mb-4 text-sm">{error}</p>
                    <button
                      onClick={fetchMenu}
                      className="px-4 py-2 text-sm text-white rounded-lg"
                      style={{ backgroundColor: theme.primary }}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 min-h-0 overflow-hidden">
                  {/* ─── Desktop Left Sidebar: Category List (hidden below md) ─── */}
                  <div
                    className="hidden md:flex flex-col flex-shrink-0 border-r overflow-y-auto pos-hide-scrollbar"
                    style={{ width: '160px', borderColor: 'var(--border-subtle)', backgroundColor: '#FAFBFC' }} /* basil-todo */
                  >
                    <div className="px-2 py-2 border-b flex justify-center" style={{ borderColor: 'var(--border-subtle)' }}>
                      {dietaryFilterControl}
                    </div>
                    <button
                      onClick={() => { setSelectedCategory(null); setSearchQuery(''); }}
                      className="text-left px-3 py-2.5 text-sm font-medium transition-colors border-b"
                      style={{
                        backgroundColor: selectedCategory === null && !searchQuery ? theme.primary : 'transparent',
                        color: selectedCategory === null && !searchQuery ? 'white' : '#374151',
                        borderColor: 'var(--border-subtle)'
                      }}
                    >
                      All Items
                    </button>
                    {menuCategories.map((category) => {
                      const isActive = selectedCategory === category.id && !searchQuery;
                      const itemCount = (category.items || []).length;
                      return (
                        <button
                          key={category.id}
                          onClick={() => { setSelectedCategory(category.id); setSearchQuery(''); }}
                          className="text-left px-3 py-2 text-sm transition-colors border-b flex items-center justify-between"
                          style={{
                            backgroundColor: isActive ? theme.primary : 'transparent',
                            color: isActive ? 'white' : '#374151',
                            fontWeight: isActive ? 600 : 400,
                            borderColor: 'var(--border-subtle)'
                          }}
                        >
                          <span className="truncate">
                            <span className="mr-1">{getCategoryEmoji(category.name)}</span>
                            {category.name}
                          </span>
                          <span
                            className="text-[10px] ml-1 flex-shrink-0 rounded-full px-1.5"
                            style={{ color: isActive ? 'rgba(255,255,255,0.7)' : '#9CA3AF' }}
                          >
                            {itemCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* ─── Mobile/Tablet: Horizontal Category Tabs (hidden on md+) ─── */}
                  {/* Center content area with tabs + grid */}
                  <div className="flex-1 flex flex-col min-h-0 min-w-0">
                    {/* Category tabs (mobile/tablet only) */}
                    <div className="md:hidden border-b px-2 py-2 flex-shrink-0 flex items-center gap-1.5" style={{ borderColor: 'var(--border-subtle)' }}>
                      <div className="flex-1 min-w-0">
                      {searchQuery ? (
                        <div className="flex items-center justify-between px-2">
                          <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>Results for "{searchQuery}"</p>
                          <button
                            onClick={() => setSearchQuery('')}
                            className="text-xs font-medium px-2 py-0.5 rounded"
                            style={{ color: theme.primary }}
                          >
                            Clear
                          </button>
                        </div>
                      ) : (
                        <WindowsScrollArrows forceShow>
                          <div className="flex gap-1.5 overflow-x-auto pos-hide-scrollbar">
                            <button
                              onClick={() => setSelectedCategory(null)}
                              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                              style={{
                                backgroundColor: selectedCategory === null ? theme.primary : '#F3F4F6',
                                color: selectedCategory === null ? 'white' : '#6B7280',
                              }}
                            >
                              All
                            </button>
                            {menuCategories.map((category) => {
                              const isSelected = selectedCategory === category.id;
                              return (
                                <button
                                  key={category.id}
                                  onClick={() => setSelectedCategory(category.id)}
                                  className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                                  style={{
                                    backgroundColor: isSelected ? theme.primary : '#F3F4F6',
                                    color: isSelected ? 'white' : '#6B7280',
                                  }}
                                >
                                  <span className="mr-1">{getCategoryEmoji(category.name)}</span>
                                  {category.name}
                                </button>
                              );
                            })}
                          </div>
                        </WindowsScrollArrows>
                      )}
                      </div>
                      {dietaryFilterControl}
                    </div>

                    {/* ─── Item Grid ─── */}
                    <div
                      ref={scrollContainerRef}
                      className="flex-1 overflow-y-auto px-2.5 py-2 pos-hide-scrollbar"
                      style={{ paddingBottom: window.innerWidth < 768 ? '80px' : '12px' }}
                    >
                      {/* Active category header (desktop only) */}
                      {!searchQuery && selectedCategory && (
                        <div className="hidden md:flex items-center gap-1.5 mb-2 px-1">
                          <span className="text-sm">{getCategoryEmoji(menuCategories.find(c => c.id === selectedCategory)?.name || '')}</span>
                          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                            {menuCategories.find(c => c.id === selectedCategory)?.name || 'All Items'}
                          </span>
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>({filteredItems.length})</span>
                        </div>
                      )}
                      {searchQuery && (
                        <div className="hidden md:flex items-center gap-1.5 mb-2 px-1">
                          <MagnifyingGlassIcon className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
                          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {filteredItems.length} results for "{searchQuery}"
                          </span>
                        </div>
                      )}

                      {filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                          <MagnifyingGlassIcon className="w-10 h-10 mx-auto mb-2" style={{ color: '#D1D5DB' }} /> {/* basil-todo */}
                          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            {dietaryFilter !== 'ALL'
                              ? `No ${DIETARY_FILTER_LABELS[dietaryFilter].toLowerCase()} items here`
                              : 'No items found'}
                          </p>
                          {dietaryFilter !== 'ALL' && (
                            <button
                              onClick={() => setDietaryFilter('ALL')}
                              className="mt-2 text-xs font-medium px-3 py-1 rounded-full"
                              style={{ color: theme.primary, border: `1px solid ${theme.primary}` }}
                            >
                              Clear diet filter
                            </button>
                          )}
                        </div>
                      ) : (
                        <div
                          // Re-key on filter/category/search so the grid fades in
                          // when a reflow swaps its contents (no blink-swap).
                          key={`${dietaryFilter}-${selectedCategory ?? 'all'}-${searchQuery}`}
                          className="grid gap-2 sog-rise-in"
                          style={{
                            gridTemplateColumns: window.innerWidth < 640
                              ? 'repeat(3, 1fr)'
                              : window.innerWidth < 768
                                ? 'repeat(4, 1fr)'
                                : 'repeat(auto-fill, minmax(100px, 1fr))'
                          }}
                        >
                          {filteredItems.map(item => {
                            const itemStyle = getItemStyle(item.name);
                            const totalQty = getTotalItemQuantity(item.id);
                            const isUnavailable = item.isAvailable === false;

                            return (
                              <button
                                key={item.id}
                                onClick={() => handleItemTap(item)}
                                disabled={isUnavailable}
                                className="relative flex flex-col items-center rounded-lg p-2 transition-all text-center active:scale-95"
                                style={{
                                  backgroundColor: isUnavailable ? '#F9FAFB' : 'white',
                                  border: totalQty > 0 ? `2px solid ${theme.primary}` : '1px solid #E5E7EB',
                                  opacity: isUnavailable ? 0.5 : 1,
                                  cursor: isUnavailable ? 'not-allowed' : 'pointer',
                                  minHeight: '80px',
                                  boxShadow: totalQty > 0 ? `0 1px 4px ${theme.primary}20` : 'none',
                                }}
                              >
                                {/* Quantity badge */}
                                {totalQty > 0 && (
                                  <span
                                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-bold text-white px-1"
                                    style={{ backgroundColor: 'var(--action)', fontSize: '10px' }}
                                  >
                                    {totalQty}
                                  </span>
                                )}

                                {/* Unavailable overlay */}
                                {isUnavailable && (
                                  <span className="absolute top-0.5 left-0.5 text-[8px] font-semibold px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' }}>
                                    N/A
                                  </span>
                                )}

                                {/* Dietary marker — corner badge on the compact tile */}
                                {!isUnavailable && item.dietaryType && (
                                  <span className="absolute top-1 left-1">
                                    <DietaryDot type={item.dietaryType} />
                                  </span>
                                )}

                                {/* Item image or emoji */}
                                {item.imageUrl ? (
                                  <div className="w-8 h-8 rounded-md overflow-hidden mb-1 flex-shrink-0">
                                    <MenuItemImage imageUrl={item.imageUrl} itemName={item.name} size="thumbnail" />
                                  </div>
                                ) : (
                                  <div
                                    className="w-8 h-8 rounded-md flex items-center justify-center mb-1 flex-shrink-0"
                                    style={{ background: itemStyle.gradient }}
                                  >
                                    <span className="text-base">{itemStyle.emoji}</span>
                                  </div>
                                )}

                                {/* Item name */}
                                <p
                                  className="text-[11px] font-medium leading-tight mb-0.5 w-full"
                                  style={{
                                    color: 'var(--text-secondary)',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {item.name}
                                </p>

                                {/* Price */}
                                <p className="text-[11px] font-bold" style={{ color: 'var(--text-primary)' }}>
                                  {item.portions && item.portions.length > 1
                                    ? `${formatCurrency(Math.min(...item.portions.map(p => item.price + p.priceModifier)), { showDecimals: false })}+`
                                    : formatCurrency(
                                        item.portions?.[0] ? item.price + item.portions[0].priceModifier : item.price,
                                        { showDecimals: false }
                                      )
                                  }
                                </p>

                                {/* Multi-portion indicator */}
                                {item.portions && item.portions.length > 1 && (
                                  <span className="text-[8px]" style={{ color: 'var(--text-tertiary)' }}>
                                    {item.portions.length} sizes
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ─── Desktop Right Sidebar: Cart (hidden below md) ─── */}
                  <div
                    className="hidden md:flex flex-col flex-shrink-0 border-l"
                    style={{ width: '280px', borderColor: 'var(--border-subtle)', backgroundColor: 'var(--bg-surface)' }}
                  >
                    {renderCartContent(false)}
                  </div>
                </div>
              )}

              {/* ─── Mobile/Tablet Bottom Cart Bar (hidden on md+) ─── */}
              <div className="md:hidden">
              <AnimatePresence>
                {cart.length > 0 && !showCart && (
                  <motion.div
                    key="bottom-cart-bar"
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="fixed inset-x-0 bottom-0 z-[10001] px-4 py-3 border-t"
                    style={{
                      backgroundColor: 'var(--bg-surface)',
                      borderColor: 'var(--border-subtle)',
                      paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                      boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {getTotalItemsInCart()} {getTotalItemsInCart() === 1 ? 'item' : 'items'}
                        </span>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {formatCurrency(calculateGrandTotal())}
                        </p>
                      </div>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowCart(true)}
                        className="px-5 py-2 font-semibold text-sm flex items-center gap-2"
                        style={{
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          color: 'var(--text-inverse)',
                          borderRadius: '9999px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                        }}
                      >
                        <span>Cart</span>
                        <span className="rounded-full px-1.5 py-0.5 text-xs" style={{ backgroundColor: 'rgba(255,255,255,0.25)', fontWeight: 700 }}>
                          {getTotalItemsInCart()}
                        </span>
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>

              {/* ─── Mobile/Tablet Cart Sheet (full-screen bottom sheet) ─── */}
              <div className="md:hidden">
              <AnimatePresence>
                {showCart && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-[10001] flex items-end"
                    onClick={() => setShowCart(false)}
                  >
                    <motion.div
                      ref={cartSheetRef}
                      initial={{ y: '100%' }}
                      animate={{ y: 0 }}
                      exit={{ y: '100%' }}
                      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                      drag="y"
                      dragConstraints={{ top: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(_, info) => {
                        if (info.offset.y > 100) {
                          setShowCart(false);
                        }
                      }}
                      className="w-full h-[85vh] rounded-t-2xl shadow-2xl flex flex-col"
                      style={{ backgroundColor: 'var(--bg-surface)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Drag Handle */}
                      <div className="flex justify-center pt-3 pb-1">
                        <div className="w-10 h-1 bg-gray-300 rounded-full" />
                      </div>
                      {renderCartContent(true)}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}

      {/* Standalone Portion Selector Modal */}
      {openDropdown !== null && (() => {
        const selectedItem = menuCategories
          .flatMap(cat => cat.items)
          .find(item => item.id === openDropdown);

        if (selectedItem && selectedItem.portions && selectedItem.portions.length > 1) {
          const existingQuantities: { [portionId: number]: number } = {};
          cart.forEach((cartItem) => {
            if (cartItem.menuItemId === selectedItem.id && cartItem.portionId) {
              existingQuantities[cartItem.portionId] = (existingQuantities[cartItem.portionId] || 0) + cartItem.quantity;
            }
          });

          return (
            <StandalonePortionSelector
              item={{
                id: selectedItem.id,
                name: selectedItem.name,
                portions: selectedItem.portions,
                allowsSpiceCustomization: selectedItem.allowsSpiceCustomization,
                spiceLevel: selectedItem.spiceLevel
              }}
              existingQuantities={existingQuantities}
              onClose={() => setOpenDropdown(null)}
              onAddToCart={(selections) => {
                setCart(prev => {
                  const filteredCart = prev.filter(item => item.menuItemId !== selectedItem.id);
                  const newCartItems = selections
                    .filter(selection => selection.quantity > 0)
                    .map(selection => ({
                      menuItemId: selectedItem.id,
                      menuItemDetails: selectedItem,
                      quantity: selection.quantity,
                      price: selection.price,
                      portionId: selection.portionId,
                      portionName: selection.portionName || '',
                      requestedSpiceLevel: selection.spiceLevel
                    }));
                  return [...filteredCart, ...newCartItems];
                });
                setOpenDropdown(null);
              }}
            />
          );
        }

        // Single-portion item with spice customization only
        if (selectedItem && selectedItem.allowsSpiceCustomization) {
          const currentSpice = selectedSpiceLevels[selectedItem.id] || selectedItem.spiceLevel || null;
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[100001] flex items-center justify-center p-4"
              onClick={() => setOpenDropdown(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>{selectedItem.name}</h3>
                  <CloseButton onClick={() => setOpenDropdown(null)} size="sm" />
                </div>
                <SpiceSelector
                  selectedLevel={currentSpice}
                  onChange={(level) => {
                    setSelectedSpiceLevels(prev => ({ ...prev, [selectedItem.id]: level }));
                  }}
                  size="small"
                />
                <button
                  onClick={() => {
                    handleAddToCart(selectedItem);
                    setOpenDropdown(null);
                  }}
                  className="w-full mt-4 py-2.5 rounded-lg font-semibold text-sm text-white"
                  style={{ backgroundColor: theme.primary, border: 'none' }}
                >
                  Add to Cart
                </button>
              </motion.div>
            </motion.div>
          );
        }

        return null;
      })()}
    </AnimatePresence>
  );
};

export default ImprovedVendorOrderFormModal;
